import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { PRAY_ARTIST_ID } from '@pray/shared/constants';
import { useAuth } from '@pray/shared/hooks/useAuth';
import studioService from '@pray/shared/services/studioService';
import { useArtist, useArtists } from '@pray/shared/services/studioService/artist';
import type { ArtistResponse } from '@pray/shared/services/studioService/artist/types';
import type { CreditAccount } from '@pray/shared/services/studioService/credits';
import storage from '@pray/shared/utils/storage';

import { ENTERPRISE_PLAN_SKU, LOCAL_STORAGE } from 'constants.js';
import { useCreditAccount } from 'hooks/credits';
import userIdentify from 'utils/analytics/userIdentify';

import { updateCreditAccountRef } from '../services/errorHandlers';

// Types
type Artist = ArtistResponse & {
  isResolved?: boolean;
};

export const initialArtistData: Artist = {
  id: '',
  name: '',
  handle: '',
  description: null,
  featured_content_id: null,
  gift_code_id: null,
  profile_image_url: null,
  profile_vertical_image_url: null,
  profile_horizontal_image_url: null,
  facecoin_image_url: null,
  giving_external_url: null,
  is_studio_analytics_enabled: false,
  is_verified: false,
  is_approved: false,
  google_place_id: null,
  latitude: null,
  longitude: null,
  location_name: null,
  location_address: null,
  is_location_search_enabled: false,
  giving_preference: null,
  is_giving_turned_off: false,
};

type StudioContextProviderProps = {
  children: React.ReactNode;
};

// Extracts the leader id from a /leaders/:artistId path, decoded like router
// params. 'profile' is a literal artist-less route (/leaders/profile/*),
// never an artist id. Shared so the route shape and the 'profile' exclusion
// live in one place (also used by SideBar's redirect guard).
export const getArtistIdFromPath = (pathname: string): string => {
  const match = pathname.match(/^\/leaders\/([^/]+)/);
  const artistId = match?.[1] ? decodeURIComponent(match[1]) : '';
  return artistId !== 'profile' ? artistId : '';
};

// StudioContextProvider mounts outside the Router, so the deep-link leader id
// is read straight from the location.
const getUrlArtistId = (): string => getArtistIdFromPath(window.location.pathname);

export default function StudioContextProvider({ children }: StudioContextProviderProps) {
  const queryClient = useQueryClient();
  const { setDomainId, setUserAndPermissions, user } = useAuth();
  // Seed the selection from the URL so a deep link to /leaders/B/... wins
  // deterministically over the stored last leader: activeArtistId is B from
  // the very first render, so the stored artist's query never even starts.
  const [selectedArtist, _setSelectedArtist] = useState<Artist>(() => {
    const urlArtistId = getUrlArtistId();
    return urlArtistId ? { ...initialArtistData, id: urlArtistId } : initialArtistData;
  });
  const setSelectedArtist = (artist: Artist | null) => _setSelectedArtist(artist ?? initialArtistData);
  const [deniedUrlArtistId, setDeniedUrlArtistId] = useState('');
  // Only fetch credits for a committed (server-validated) selection; the URL
  // seed is unvalidated and may be logged-out or denied.
  const { data: creditAccount } = useCreditAccount({ artistId: selectedArtist?.isResolved ? selectedArtist.id : '' });

  const storedArtistId = storage.getFromStorage(LOCAL_STORAGE.SELECTED_ARTIST_ID);
  const activeArtistId = selectedArtist?.id?.toString() || storedArtistId;

  const artistsQuery = useArtists();
  const artistQuery = useArtist(activeArtistId);

  const artists = (artistsQuery.data as { data: ArtistResponse[] } | undefined)?.data || [];
  const artist = (artistQuery.data as { data: ArtistResponse } | undefined)?.data;
  const isArtistLoading = artistsQuery.isLoading || artistQuery.isLoading;
  const isArtistError = artistQuery.isError;

  const setArtist = (artist: Artist) => {
    setDomainId(artist.id.toString());
    setSelectedArtist({ ...artist, isResolved: true });
    storage.upsertToStorage(LOCAL_STORAGE.SELECTED_ARTIST_ID, artist.id.toString());
  };

  const isEnterprisePlan = useMemo(() => {
    const creditPlanSku = creditAccount?.creditsPlan?.sku;
    if (!creditPlanSku) return undefined;
    return creditPlanSku === ENTERPRISE_PLAN_SKU;
  }, [creditAccount]);

  const isPrayArtist = useMemo(() => {
    return selectedArtist?.id === PRAY_ARTIST_ID;
  }, [selectedArtist?.id]);

  const isArtistApproved = selectedArtist.is_approved;

  useEffect(() => {
    if (user?.is_registered) {
      userIdentify.identifyUser(user);
    }
  }, [user?.is_registered]);

  useEffect(() => {
    updateCreditAccountRef(creditAccount);
  }, [creditAccount]);

  useEffect(() => {
    // Don't resolve until the user is registered: while logged out both
    // queries are disabled (so isArtistLoading is false) and resolving here
    // would stomp a URL-seeded selection with the empty sentinel before the
    // user gets a chance to log in.
    if (!user?.is_registered) return;
    if (isArtistLoading) return;

    if (artist) {
      setArtist(artist);
      return;
    }

    if (isArtistError) {
      const failingId = activeArtistId;

      // The accessible-artists list is the access source of truth: if the
      // failing id is in it, the single fetch failed transiently (retry is
      // disabled), not an access denial - commit the list entry so the user
      // keeps their own leader instead of being bounced to a fallback.
      const failingArtistFromList = artists.find((a) => a.id === failingId);
      if (failingArtistFromList) {
        setArtist(failingArtistFromList);
        return;
      }

      // Only drop the stored id when the stored id itself failed; a foreign
      // deep link must not wipe the user's legitimate last-leader id. This
      // runs before the fallback commit so it cannot clobber the fallback's
      // own storage write inside setArtist.
      if (storedArtistId && storedArtistId === failingId) {
        storage.removeFromStorage(LOCAL_STORAGE.SELECTED_ARTIST_ID);
      }

      const fallback = artists.find((a) => a.id === storedArtistId) || artists[0];

      if (fallback) {
        setArtist(fallback);
        if (getUrlArtistId() === failingId) {
          // Deep link to an inaccessible or deleted leader: StudioPage shows
          // a toast and rewrites the URL to the fallback leader's dashboard.
          setDeniedUrlArtistId(failingId);
        }
      } else {
        setSelectedArtist({ ...initialArtistData, isResolved: true });
      }
      return;
    }

    if (artists.length) {
      setArtist(artists[0]);
    } else {
      setSelectedArtist({ ...initialArtistData, isResolved: true });
    }
  }, [artist, isArtistLoading, isArtistError, user?.is_registered]);

  const authenticate = async (): Promise<void> => {
    const { user, permissions } = await studioService.auth.authenticate();
    user.is_permissions_resolved = true;
    setUserAndPermissions(user, permissions);
  };

  const resetContextData = (): void => {
    setSelectedArtist(initialArtistData);
    queryClient.clear();
  };

  const contextValue: StudioContextValue = {
    artists,
    selectedArtist,
    isArtistLoading,
    creditAccount,
    isEnterprisePlan,
    isPrayArtist,
    isArtistApproved,
    deniedUrlArtistId,
    clearDeniedUrlArtistId: () => setDeniedUrlArtistId(''),
    authenticate,
    setSelectedArtist,
    resetContextData,
  };

  return <StudioContext.Provider value={contextValue}>{children}</StudioContext.Provider>;
}

type StudioContextValue = {
  artists: Artist[];
  selectedArtist: Artist;
  creditAccount: CreditAccount | undefined;
  isEnterprisePlan: boolean | undefined;
  isPrayArtist: boolean;
  isArtistLoading: boolean;
  isArtistApproved: boolean;
  deniedUrlArtistId: string;
  clearDeniedUrlArtistId: () => void;
  authenticate: () => Promise<void>;
  setSelectedArtist: (artist: Artist | null) => void;
  resetContextData: () => void;
};

// Context
export const StudioContext = createContext<StudioContextValue | null>(null);

export const useStudioContext = (): StudioContextValue => {
  const context = useContext(StudioContext);

  if (!context) {
    throw new Error('useStudioContext must be used within a StudioContextProvider');
  }

  return context;
};
